import { create } from 'zustand';
import { storage } from '@/services/storage';
import { login as loginApi, logout as logoutApi, fetchMe } from '@/services/auth';
import type { User } from '@/types/api';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hydrate: () => Promise<void>;
  clearError: () => void;
  updateCredits: (remaining: number) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: storage.getUser<User>(),
  isAuthenticated: !!storage.getAccess(),
  isLoading: false,
  error: null,

  async login(email, password) {
    set({ isLoading: true, error: null });
    try {
      const resp = await loginApi(email.trim(), password);
      set({ user: resp.user, isAuthenticated: true, isLoading: false });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Login failed';
      // Prefer axios error shape if available.
      const anyErr = e as { response?: { data?: { message?: string } } };
      const apiMsg = anyErr?.response?.data?.message;
      set({ isLoading: false, error: apiMsg ?? msg });
      throw e;
    }
  },

  async logout() {
    await logoutApi();
    set({ user: null, isAuthenticated: false });
  },

  async hydrate() {
    if (!storage.getAccess()) return;
    try {
      const user = await fetchMe();
      storage.setUser(user);
      set({ user, isAuthenticated: true });
    } catch {
      storage.clear();
      set({ user: null, isAuthenticated: false });
    }
  },

  clearError() {
    set({ error: null });
  },

  updateCredits(remaining: number) {
    set((s) => s.user ? { user: { ...s.user, credits: remaining } } : {});
  },
}));

// React to refresh-failure events emitted by the axios interceptor.
window.addEventListener('auth:expired', () => {
  useAuthStore.setState({ user: null, isAuthenticated: false });
});
